import { EMPTY_OBJECT } from '..';

interface Options {
  isSync?: boolean;
  selectionRange?: 'all' | 'filename';
  selector?: string;
}

const DEFAULT_SELECTOR = '*';
const SELECTABLE_INPUT_TYPES = new Set(['password', 'search', 'tel', 'text', 'url']);
const CHECK_NEXT_NODE_EX = 'Check next node';

/**
 *
 * @param {HTMLElement | Document} container The HTML element to start searching
 * @param {object} options The options to customize searching:
 * - `isSync`: determines if the element search should start immediately (sync) or wait until the next animation frame (async). Defaults to `true`.
 * - `selector`: a CSS selector to further narrow the results
 * - `selectionRange`: set the selection of an input value after focus
 * @returns {HTMLElement | undefined} If sync, the element that received focus. If no element focused or async, undefined.
 */
export function focusFirstAvailable(
  container: HTMLElement | Document,
  options: Options = EMPTY_OBJECT,
): HTMLElement | undefined {
  if (!container) {
    console.warn('focusFirstAvailable: no container provider');
    return;
  }

  const { selector = DEFAULT_SELECTOR, selectionRange, isSync = true } = options;

  function walk() {
    const walker = document.createTreeWalker(container, NodeFilter.SHOW_ELEMENT);
    let currentNode = walker.currentNode as HTMLElement;

    while (currentNode) {
      try {
        const isValidNode = 'type' in currentNode ? currentNode.type !== 'hidden' : true; // Safari will set focus to `<input type="hidden" />` ¯\_(ツ)_/¯
        if (isValidNode && currentNode.matches?.(selector)) {
          currentNode.focus?.();

          if (document.activeElement !== currentNode) {
            throw new Error(CHECK_NEXT_NODE_EX);
          }

          // using a ternary so TS can properly infer the node type
          const selectionNode =
            currentNode instanceof HTMLTextAreaElement ||
            (currentNode instanceof HTMLInputElement &&
              SELECTABLE_INPUT_TYPES.has(currentNode.type))
              ? currentNode
              : null;

          if (selectionNode) {
            if (selectionRange === 'all') {
              selectionNode.select();
            } else if (selectionRange === 'filename') {
              selectionNode.setSelectionRange(0, selectionNode.value.lastIndexOf('.'));
            }
          }

          return currentNode;
        }
      } catch (ex) {
        /*
        NOTE: we should only get an exception if:
        - an ill-formed selector is provided; or
        - in tests, JSDOM doesn't support the selector (e.g. :has() is currently not well supported)
        - we force throw one to check the next node (so we ignore that)
        */
        if (ex instanceof Error && ex.message !== CHECK_NEXT_NODE_EX) {
          console.error(ex.message);
        }
      }

      currentNode = walker.nextNode() as HTMLElement;
    }
  }

  if (isSync) {
    return walk();
  }

  window.requestAnimationFrame(walk);
}
